import * as React from 'react';
import * as CreateInstallmentPlan from 'core/gql/mutations/create-installment-plan';
import * as UpdateInstallmentPlan from 'core/gql/mutations/update-installment-plan';
import * as ListInstallmentPlans from 'core/gql/queries/list-installment-plans';
import { Button, Radio } from '@teamsnap/teamsnap-ui';
import './InstallmentsDrawer.scss';
import { useForm } from 'react-hook-form';
import FormElementHook from 'components/form/form-element-hook';
import { InstallmentPlan } from './types';

import {
  DEPOSIT_TYPE_OPTIONS,
  WEEKLY_OPTIONS,
  INSTALLMENT_PLAN_LABELS,
  MONTHLY_OPTIONS,
  isMonthly,
  getFrequencyOptions,
  joinFrequencyValues,
  splitFrequencyValues,
  getPlanSummary,
  formatToDollarAmount,
  formatToPercentageAmount,
} from './installment-utils';
import { useMutation } from '@apollo/client';
import { reactiveFeedbackMessage } from 'core/apollo-cache';

const CreateElement = FormElementHook as new () => FormElementHook;

type InstallmentsDrawerProps = {
  scopeId: number;
  scopeType: string;
  isOpen: boolean;
  onClose: () => void;
  plan: InstallmentPlan | null;
  currency: string;
  currencySymbol: string;
};

const DEFAULT_VALUES = {
  name: '',
  depositType: 'amount',
  depositAmount: '',
  installmentCount: '',
  frequency: 'monthly_1',
  withdrawDay: '1',
  withdrawBasedOnCheckout: 'yes',
};

const InstallmentsDrawer = ({
  scopeId,
  scopeType,
  onClose,
  plan,
  currency,
  currencySymbol,
}: InstallmentsDrawerProps) => {
  const planForm = useForm({
    defaultValues: plan?.id
      ? {
          name: plan.name,
          depositType: plan.depositType,
          depositAmount:
            plan.depositType === 'amount'
              ? formatToDollarAmount(plan.depositAmount)
              : formatToPercentageAmount(plan.depositAmount),
          installmentCount: plan.installmentCount.toString(),
          frequency: joinFrequencyValues(plan.frequencyType, plan.frequencyAmount),
          withdrawDay: plan.withdrawDay?.toString(),
          withdrawBasedOnCheckout: plan.withdrawBasedOnCheckout ? 'yes' : 'no',
        }
      : DEFAULT_VALUES,
  });
  const { register, handleSubmit } = planForm;

  const [createInstallmentPlan, createInstallmentPlanResponse] = useMutation(CreateInstallmentPlan.MUTATION, {
    refetchQueries: [{ query: ListInstallmentPlans.QUERY, variables: { scopeId: +scopeId, scopeType: scopeType } }],
  });

  const [updateInstallmentPlan, updateInstallmentPlanResponse] = useMutation(UpdateInstallmentPlan.MUTATION, {
    refetchQueries: [{ query: ListInstallmentPlans.QUERY, variables: { scopeId: +scopeId, scopeType: scopeType } }],
  });

  const isSubmitting = createInstallmentPlanResponse.loading || updateInstallmentPlanResponse.loading;

  const frequency = planForm.watch('frequency');
  const watchDepositType = planForm.watch('depositType');
  const watchWithdrawDate = planForm.watch('withdrawBasedOnCheckout');

  planForm.watch(); // so plan summary updates on every change
  const planSummary = getPlanSummary({ ...planForm.getValues(), currencySymbol });

  const saveOrCreatePlan = async (values) => {
    const depositAmountValueInCents = parseFloat(values.depositAmount) * 100;

    const preparedData = {
      name: values.name,
      depositType: values.depositType,
      depositAmount: depositAmountValueInCents,
      installmentCount: parseInt(values.installmentCount),
      withdrawDay: values.withdrawDay,
      withdrawBasedOnCheckout: values.withdrawBasedOnCheckout === 'yes',
      ...splitFrequencyValues(values.frequency),
    };

    let mutation;
    if (plan && plan.id) {
      preparedData['id'] = plan.id;
      preparedData['enabled'] = plan.enabled;
      mutation = updateInstallmentPlan;
    } else {
      preparedData['scopeId'] = scopeId;
      preparedData['scopeType'] = scopeType;
      preparedData['enabled'] = true;
      mutation = createInstallmentPlan;
    }

    try {
      await mutation({ variables: preparedData });

      reactiveFeedbackMessage({
        message: `Payment Plan "${values.name}" has been ${plan?.id ? 'updated' : 'created'}`,
        isError: false,
        autoclose: true,
      });
    } catch (error) {
      console.error(error);
      reactiveFeedbackMessage({
        message: 'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
    }

    planForm.reset();
    onClose();
  };

  return (
    <div className="Installments__form u-padSidesLg u-flex u-flexCol" id="installments-drawer">
      <div className="u-padSidesMd u-padEndsMd u-flex u-flexAlignItemsCenter u-flexJustifyBetween">
        <h3>{plan ? `Edit ${plan.name}` : 'Create Payment Plan'}</h3>
        <Button
          type="link"
          icon="dismiss"
          mods="u-colorNeutral8"
          onClick={() => {
            onClose();
          }}
        />
      </div>
      <div className="u-padSidesMd u-flexGrow1">
        <form>
          <div className="u-flex Installments__field-row">
            <div className="u-size5of12 Installments__field-label-col">
              <div className="Installments__label-large">{INSTALLMENT_PLAN_LABELS.name}</div>
            </div>
            <div className="u-size7of12 Installments__field-input-col">
              <CreateElement
                disabled={isSubmitting}
                inputRef={register({
                  required: 'You must enter a name for this plan.',
                })}
                useHookResponse={planForm}
                maxLength={100}
                type="text"
                labelHidden
                field="name"
                label={INSTALLMENT_PLAN_LABELS.name}
                placeholder="e.g. 6 Payment Plan"
              />
            </div>
          </div>
          <div className="u-flex Installments__field-row">
            <div className="u-size5of12 Installments__field-label-col">
              <div className="Installments__label-large">{INSTALLMENT_PLAN_LABELS.depositType}</div>
            </div>
            <div className="u-size7of12 Installments__field-input-col">
              <CreateElement
                disabled={isSubmitting}
                inputRef={register({})}
                useHookResponse={planForm}
                type="select"
                options={DEPOSIT_TYPE_OPTIONS}
                labelHidden
                field="depositType"
                label={INSTALLMENT_PLAN_LABELS.depositType}
              />
              {watchDepositType === 'amount' && (
                <>
                  <div className="Installments__input-compound">
                    <span className="field-prefix">{currencySymbol}</span>
                    <CreateElement
                      disabled={isSubmitting}
                      inputRef={register({
                        required: 'You must enter a deposit amount of 6.00 or more',
                        min: 6,
                      })}
                      useHookResponse={planForm}
                      type="number"
                      labelHidden
                      field="depositAmount"
                      label={INSTALLMENT_PLAN_LABELS.depositAmount}
                      placeholder="0.00"
                      numberInputAttributes={{ min: 6, step: 0.01 }}
                    />
                    <span className="field-suffix">{currency}</span>
                  </div>
                </>
              )}
              {watchDepositType === 'percentage' && (
                <>
                  <div className="Installments__input-compound">
                    <CreateElement
                      disabled={isSubmitting}
                      inputRef={register({
                        required: 'You must enter a deposit percentage between 1 and 99',
                        min: 1,
                        max: 99,
                      })}
                      useHookResponse={planForm}
                      type="number"
                      labelHidden
                      field="depositAmount"
                      label={INSTALLMENT_PLAN_LABELS.depositAmount}
                      placeholder="0"
                      numberInputAttributes={{ min: 1, step: 1, max: 99 }}
                    />
                    <span className="field-suffix">%</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="u-flex Installments__field-row">
            <div className="u-size5of12 Installments__field-label-col">
              <div className="Installments__label-large">{INSTALLMENT_PLAN_LABELS.installmentCount}</div>
              <div className="Installments__label-helptext">Maximum 25 installments</div>
            </div>
            <div className="u-size7of12 Installments__field-input-col">
              <CreateElement
                disabled={isSubmitting}
                inputRef={register({
                  min: 1,
                  max: 25,
                  required: 'You must choose a number of installments',
                })}
                useHookResponse={planForm}
                type="number"
                labelHidden
                placeholder="0"
                field="installmentCount"
                label={INSTALLMENT_PLAN_LABELS.installmentCount}
                numberInputAttributes={{ min: 1, max: 25, step: 1 }}
              />
            </div>
          </div>
          <div className="u-flex Installments__field-row">
            <div className="u-size5of12 Installments__field-label-col">
              <div className="Installments__label-large">{INSTALLMENT_PLAN_LABELS.frequency}</div>
            </div>
            <div className="u-size7of12 Installments__field-input-col">
              <CreateElement
                disabled={isSubmitting}
                inputRef={register({})}
                useHookResponse={planForm}
                type="select"
                options={getFrequencyOptions()}
                labelHidden
                field="frequency"
                label={INSTALLMENT_PLAN_LABELS.frequency}
                numberInputAttributes={{ min: 1, max: 24, step: 1 }}
              />
            </div>
          </div>
          <div className="u-flex Installments__field-row">
            <div className="u-size5of12 Installments__field-label-col">
              <div className="Installments__label-large">{INSTALLMENT_PLAN_LABELS.withdrawDay}</div>
              <div className="Installments__label-helptext">
                If installment falls on the last day of the month, it will continue to be the last day. Example: Feb.
                28, March 31.
              </div>
            </div>
            <div className="u-size7of12 Installments__field-input-col">
              <CreateElement
                disabled={isSubmitting || watchWithdrawDate === 'yes'}
                inputRef={register({})}
                useHookResponse={planForm}
                type="select"
                options={isMonthly(frequency) ? MONTHLY_OPTIONS : WEEKLY_OPTIONS}
                isInline
                field="withdrawDay"
                label={isMonthly(frequency) && 'of the month'}
                className="u-flexRowReverse"
              />
              <div className="Installments__field-checkout-radio-group">
                <Radio
                  disabled={isSubmitting}
                  name="withdrawBasedOnCheckout"
                  id="withdrawBasedOnCheckout-no"
                  label=""
                  inputProps={{ ref: register({}), value: 'no' }}
                />
                <Radio
                  disabled={isSubmitting}
                  name="withdrawBasedOnCheckout"
                  id="withdrawBasedOnCheckout-yes"
                  label={INSTALLMENT_PLAN_LABELS.withdrawBasedOnCheckout}
                  inputProps={{ ref: register({}), value: 'yes' }}
                />
              </div>
            </div>
          </div>
        </form>
        {planSummary && (
          <div className="Installments__plan-summary u-padTopMd">
            <h3 className="Installments__label-large u-spaceBottomSm">Plan Summary</h3>

            <span>{planSummary}</span>
            <small>
              {watchWithdrawDate === 'yes' &&
                'If checkout date was April 12, payments will withdraw on the 12th of the month'}
            </small>
          </div>
        )}
      </div>
      <div className="u-flex u-flexJustifyEnd u-padSidesMd u-padEndsMd ">
        <div className="button-list">
          <Button
            isDisabled={isSubmitting}
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>

          <Button
            isDisabled={isSubmitting}
            type="submit"
            color="primary"
            onClick={(e) => {
              e.preventDefault();
              handleSubmit(saveOrCreatePlan)();
            }}
          >
            {plan?.id ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstallmentsDrawer;
